import React, { useEffect, useState } from 'react';
import { Container } from '@common/components/container';
import { QuestionsList } from './question-list';
import { QuestionContainer } from './question-container';
import PhonePreview from '@modules/performance/components/phone-preview/phone-preview';
import { Route, useHistory, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { PerformanceQuestionType } from '@modules/performance/definitions';
import Placeholder from '@common/components/placeholder';
import { Button } from '@common/components/button';
import { usePerformanceStore } from '@modules/performance/store/store';
import { ObjectValues } from '@common/types/util-types';
import { PerformanceFormComponent } from '@modules/performance/types';
import { QuestionDropdown } from '@modules/performance/components/question-dropdown/question-dropdown';
import { EvaluationFormTypes } from '@common/definitions';

type FormQuestionsProps = {
  match: any;
  createQuestion: any;
  openQuestion: any;
  orderQuestions: any;
  saveOrderQuestions: any;
  deleteQuestion: any;
  duplicateQuestion: any;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  basePath: string;
  isSubmitting?: boolean;
};

export const FormQuestions = ({
  match,
  control,
  setValue,
  watch,
  basePath,
  isSubmitting,
  ...fns
}: FormQuestionsProps) => {
  const history = useHistory();
  const { form, drafts } = usePerformanceStore();
  const { search } = useLocation();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('Employee');
  const tabs = [t('performance:only_employee'), t('performance:manager')];

  useEffect(() => {
    if (
      !match.params.componentId &&
      form?.components &&
      form?.components.length > 0
    ) {
      history.replace(`${match.url}/${form?.components[0].id}${search}`);
    }
  }, [form?.components, search]);

  if (form?.components?.length === 0) {
    return (
      <Placeholder
        image="/static/images/modules-placeholder.svg"
        title={t('forms:form_placeholder_title')}
        description={t('forms:form_placeholder_description')}
        action={
          <QuestionDropdown
            toggle={
              <Button type="primary" size="large">
                {t('performance:add_block')}
              </Button>
            }
            enabledComponents={[
              PerformanceQuestionType.OPEN_QUESTION,
              PerformanceQuestionType.COMPETENCIES,
              PerformanceQuestionType.CONTENT_MODULE,
              PerformanceQuestionType.TALENT_REVIEW,
            ]}
            onSelect={(item) => fns.createQuestion({
                type: item?.type as ObjectValues<
                  typeof PerformanceQuestionType
                >,
              })}
          />
        }
      />
    );
  }

  return (
    <Container.Content horizontal>
      <div className="tw-flex tw-flex-col tw-w-[300px] tw-gap-5 tw-mr-6 tw-bg-white tw-p-4 tw-rounded-lg tw-h-full">
        {(() => {
          const focusedId = match.params.componentId;
          const watchedText = watch('text');
          const listComponents = (form?.components || []).map((component) => {
            if (component.type !== PerformanceQuestionType.OPEN_QUESTION) {
              return component;
            }

            const isFocused = component.id === focusedId;
            const draft = drafts?.[component.id] || {};

            return {
              ...component,
              settings: {
                ...component.settings,
                text: (isFocused
                  ? typeof watchedText !== 'undefined'
                    ? watchedText
                    : component.settings?.text
                  : typeof draft.text !== 'undefined'
                  ? draft.text
                  : component.settings?.text) as string,
              },
            } as any;
          });

          return (
            <QuestionsList
              componentId={match.params.componentId}
              components={listComponents as PerformanceFormComponent[]}
              openQuestion={fns.openQuestion}
              handleDrag={fns.orderQuestions}
              onReorderCommit={(order) => fns.saveOrderQuestions(order)}
              addQuestion={(
                type: ObjectValues<typeof PerformanceQuestionType>
              ) => fns.createQuestion({ type })}
              deleteQuestion={fns.deleteQuestion}
              duplicateQuestion={fns.duplicateQuestion}
            />
          );
        })()}
      </div>

      {form && (
        <Route
          path={[
            `${basePath}/performance/forms/:id/edit/components/:componentId`,
          ]}
          render={(props) => {
            return (
              <>
                <div className="tw-flex tw-flex-1 tw-flex-col tw-w-[350px] tw-bg-white tw-p-5 tw-gap-5 tw-rounded-lg tw-h-full">
                  <QuestionContainer
                    key={props.match.params.componentId}
                    addComponent={fns.createQuestion}
                    component={form?.components.find(
                      (component) => component.id === props.match.params.componentId
                    )}
                    deleteComponent={fns.deleteQuestion}
                    control={control}
                    setValue={setValue}
                    watch={watch}
                    isSubmitting={isSubmitting}
                    {...props}
                  />
                </div>
                <div className="tw-flex tw-flex-col tw-w-[32%] tw-gap-6">
                  {form.type === EvaluationFormTypes.MANAGER_EMPLOYEE && (
                    <div className="tw-mx-10">
                      <div
                        className="tw-bg-gray-100 tw-rounded-lg tw-p-1 tw-flex
                   tw-border tw-border-solid tw-border-neutral-200"
                      >
                        {tabs.map((tab) => (
                          <div
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`tw-flex-1 tw-py-2 tw-text-center tw-px-4 tw-rounded-md 
                            tw-text-base tw-font-medium tw-transition-all tw-duration-200 tw-cursor-pointer ${
                              activeTab === tab
                                ? 'tw-bg-white tw-text-gray-textDark tw-shadow-sm'
                                : 'tw-text-gray-text hover:tw-text-gray-900'
                            }`}
                          >
                            {tab}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="tw-flex tw-items-center tw-justify-center">
                    {(() => {
                      const focusedId = props.match.params.componentId;
                      const watchedText = watch('text');
                      const watchedDescription = watch('description');
                      const watchedCategoryIds = watch('category_ids');
                      const watchedManager = watch('manager');
                      const watchedEmployee = watch('employee');
                      const watchedAnswerRequired = watch('answer_required');
                      const watchedAnswerVisible = watch('answer_visible');
                      const watchedImpactOfLoss = watch('impact_of_loss');
                      const watchedRetentionRisk = watch('retention_risk');
                      const previewComponents = (form?.components || []).map(
                        (component) => {
                          const isFocused = component.id === focusedId;
                          const draft = drafts?.[component.id] || {};
                          if (
                            component.type ===
                            PerformanceQuestionType.COMPETENCIES
                          ) {
                            const categoryIds = isFocused
                              ? watchedCategoryIds ??
                                component.settings?.category_ids
                              : typeof draft.category_ids !== 'undefined'
                              ? draft.category_ids
                              : component.settings?.category_ids;
                            return {
                              ...component,
                              settings: {
                                ...component.settings,
                                category_ids: categoryIds,
                              },
                            } as any;
                          }

                          if (component.type === PerformanceQuestionType.CONTENT_MODULE) {
                            // Always show draft or saved content, never the watched values
                            const contentComponents = typeof draft.content_components !== 'undefined'
                              ? draft.content_components
                              : component.settings?.content_components || component.settings;

                            return {
                              ...component,
                              settings: Array.isArray(contentComponents)
                                ? contentComponents
                                : { ...component.settings, content_components: contentComponents }
                            } as any;
                          }

                          if (
                            component.type ===
                            PerformanceQuestionType.TALENT_REVIEW
                          ) {
                            return {
                              ...component,
                              settings: {
                                ...component.settings,
                                impact_of_loss: isFocused
                                  ? typeof watchedImpactOfLoss !== 'undefined'
                                    ? watchedImpactOfLoss
                                    : component.settings?.impact_of_loss
                                  : typeof draft.impact_of_loss !== 'undefined'
                                  ? draft.impact_of_loss
                                  : component.settings?.impact_of_loss,
                                retention_risk: isFocused
                                  ? typeof watchedRetentionRisk !== 'undefined'
                                    ? watchedRetentionRisk
                                    : component.settings?.retention_risk
                                  : typeof draft.retention_risk !== 'undefined'
                                  ? draft.retention_risk
                                  : component.settings?.retention_risk,
                              },
                            } as any;
                          }

                          const visibility = {
                            manager: isFocused
                              ? typeof watchedManager !== 'undefined'
                                ? watchedManager
                                : component.settings?.visibility?.manager
                              : typeof draft.manager !== 'undefined'
                              ? draft.manager
                              : component.settings?.visibility?.manager,
                            employee: isFocused
                              ? typeof watchedEmployee !== 'undefined'
                                ? watchedEmployee
                                : component.settings?.visibility?.employee
                              : typeof draft.employee !== 'undefined'
                              ? draft.employee
                              : component.settings?.visibility?.employee,
                            answer_visible: isFocused
                              ? typeof watchedAnswerVisible !== 'undefined'
                                ? watchedAnswerVisible
                                : component.settings?.visibility?.answer_visible
                              : typeof draft.answer_visible !== 'undefined'
                              ? draft.answer_visible
                              : component.settings?.visibility?.answer_visible,
                          };

                          return {
                            ...component,
                            settings: {
                              ...component.settings,
                              visibility,
                              answer_required: isFocused
                                ? typeof watchedAnswerRequired !== 'undefined'
                                  ? watchedAnswerRequired
                                  : component.settings?.answer_required
                                : typeof draft.answer_required !== 'undefined'
                                ? draft.answer_required
                                : component.settings?.answer_required,
                              text: (isFocused
                                ? typeof watchedText !== 'undefined'
                                  ? watchedText
                                  : component.settings?.text
                                : typeof draft.text !== 'undefined'
                                ? draft.text
                                : component.settings?.text) as string,
                              description: isFocused
                                ? typeof watchedDescription !== 'undefined'
                                  ? watchedDescription
                                  : component.settings?.description
                                : typeof draft.description !== 'undefined'
                                ? draft.description
                                : component.settings?.description,
                            },
                          } as any;
                        }
                      );

                      const role =
                        activeTab === t('performance:only_employee')
                          ? 'employee'
                          : 'manager';
                      const filteredComponents = previewComponents.filter(
                        (component) => {
                          if (
                            component.type ===
                            PerformanceQuestionType.TALENT_REVIEW && form.type === EvaluationFormTypes.MANAGER_EMPLOYEE
                          ) {
                            return role === 'manager';
                          }

                          const visibility = component.settings?.visibility;
                          if (!visibility) return true;

                          return role === 'employee'
                            ? visibility.employee
                            : visibility.manager;
                        }
                      );

                      return (
                        <PhonePreview
                          navbarTitle={form.title}
                          showBackButton={false}
                          components={
                            filteredComponents as PerformanceFormComponent[]
                          }
                          focusedComponentId={focusedId}
                        />
                      );
                    })()}
                  </div>
                </div>
              </>
            );
          }}
        />
      )}
    </Container.Content>
  );
};
